// Stage stepper (Overview, zoom level 1): each stage with the engine's state and the reason for any wait.
import type { Stage } from "../data/types";
import { titleCase } from "../lib/format";

/** "4 done · 1 waiting on client": the engine's own counts of the stage's steps per state. */
export const countsText = (c: Record<string, number>) => Object.entries(c).map(([k, n]) => `${n} ${titleCase(k).toLowerCase()}`).join(" · ");
import { statusOf } from "../lib/status";

const DOT: Record<string, string> = {
  done: "bg-done border-done text-paper", progress: "bg-progress-bg border-progress text-progress", waiting: "bg-waiting-bg border-waiting text-waiting",
  client: "bg-client-bg border-client text-client", blocked: "bg-blocked-bg border-blocked text-blocked", stale: "bg-stale-bg border-stale text-stale",
  agent: "bg-agent-bg border-agent text-agent", neutral: "bg-paper border-line text-muted",
};
const TEXT: Record<string, string> = { done: "text-done", progress: "text-progress", waiting: "text-waiting", client: "text-client", blocked: "text-blocked", stale: "text-stale", agent: "text-agent", neutral: "text-muted" };
const GLYPH: Record<string, string> = { done: "✓", progress: "◐", waiting: "◷", client: "◉", blocked: "!", stale: "↻", agent: "✦", neutral: "" };

export function Stepper({ stages, selected, onSelect }: { stages: Stage[]; selected?: string; onSelect?: (stageId: string) => void }) {
  return (
    <ol aria-label="Stages" className="relative flex gap-0 overflow-x-auto pb-2">
      {stages.map((s, i) => {
        const st = statusOf(s.state);
        const on = s.stageId === selected;
        return (
          <li key={s.stageId} className="flex min-w-[92px] flex-1 flex-col items-center text-center">
            <div className="flex w-full items-center">
              <span className={`h-px flex-1 ${i === 0 ? "bg-transparent" : "bg-line"}`} />
              <button type="button" onClick={() => onSelect?.(s.stageId)} aria-pressed={on}
                aria-label={`${s.stageId} ${s.name}: ${st.label}${s.reason ? ` — ${s.reason}` : ""} (${countsText(s.counts)})`}
                title={s.reason ?? st.label}
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-xs font-bold ${DOT[st.kind]} ${on ? "ring-2 ring-focus ring-offset-2 ring-offset-paper" : ""}`}>
                {GLYPH[st.kind] || <span className="text-[10px]">{s.stageId.slice(1)}</span>}
              </button>
              <span className={`h-px flex-1 ${i === stages.length - 1 ? "bg-transparent" : "bg-line"}`} />
            </div>
            <span className="mt-1 font-mono text-[11px] text-muted">{s.stageId}</span>
            <span className="line-clamp-2 px-1 text-[11px] leading-tight">{s.name.split(":")[0]}</span>
            <span className={`mt-0.5 text-[10px] ${TEXT[st.kind]}`}>{st.label}</span>
          </li>
        );
      })}
    </ol>
  );
}
